/**
 * PASTE CODE (A06.5)
 *
 * Simplified deep-link page: just the invitation code input (6-8 chars).
 * For when a user arrives from a deep link without being logged in.
 * Validates the code, logs in if needed, and redirects appropriately.
 */

import React, { useState, FormEvent, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getBrandingConfig } from '@shared/config/brandingConfig';
import { useDynamicBranding } from '../hooks/useDynamicBranding';
import { supabase } from '@shared/lib/supabaseClient';
import { Button } from '@shared/ui/shadcn/ui/button';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { Input } from '@shared/ui/shadcn/ui/input';
import { Loader2, KeyRound, CheckCircle2 } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';
import { handleHandoffRedirect, setSSOCookie } from './Login';
import { createSsoTrace } from '@shared/utils/ssoTrace';
import { notifyCoreAPILogin } from '@shared/services/coreAuthService';
import { APP_SSO_CONFIGS } from '@shared/navigation/appSSOConfig';

/**
 * Helper: get Core API URL.
 */
const getCoreApiUrl = (): string => {
  const envUrl = (import.meta.env.VITE_CORE_API_URL || '').trim();
  if (envUrl) return envUrl;
  return import.meta.env.DEV ? 'http://localhost:4000' : 'https://api.elfom.com';
};

const PasteCodePage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const app = params.get('app') || 'empresa';
  const returnTo = params.get('returnTo');
  const clientOriginParam = params.get('clientOrigin');
  const prefilledCode = params.get('code') || '';

  useDynamicBranding(app);
  const branding = getBrandingConfig(app);

  const [code, setCode] = useState(prefilledCode.toUpperCase());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setNeedsLogin(true);
      }
    };
    checkSession();
  }, []);

  // Auto-submit if code came from query param and user is logged in
  useEffect(() => {
    if (prefilledCode && !needsLogin && prefilledCode.length >= 6) {
      handleSubmit({ preventDefault: () => {} } as FormEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsLogin]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = code.trim().toUpperCase();
    if (trimmed.length < 6 || trimmed.length > 8) {
      setError('El codigo debe tener entre 6 y 8 caracteres');
      return;
    }

    // If user is not logged in, redirect to login with code in returnTo
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      const loginUrl = `/login?app=${app}&returnTo=${encodeURIComponent(`/paste-code?code=${trimmed}&app=${app}`)}`;
      navigate(loginUrl);
      return;
    }

    setLoading(true);

    try {
      const coreApiUrl = getCoreApiUrl();
      const response = await fetch(`${coreApiUrl}/workspaces/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: trimmed }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        setError(errorData.message || errorData.error || 'Codigo invalido o expirado');
        setLoading(false);
        return;
      }

      setSuccess(true);

      // Handoff to app
      try {
        await notifyCoreAPILogin(session.access_token);
        await setSSOCookie(session.access_token);
      } catch (_) {}

      if (app && APP_SSO_CONFIGS[app]) {
        const trace = createSsoTrace({ app: 'accounts', feature: 'SSO_HANDOFF' });
        trace.step('PASTE_CODE_JOIN_SUCCESS', { code: trimmed });
        await handleHandoffRedirect(
          app,
          returnTo || '/dashboard',
          (msg) => setError(msg),
          trace,
          clientOriginParam,
        );
      }
    } catch (err: any) {
      setError(err?.message || 'Error de conexion. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    const loginUrl = `/login?app=${app}&returnTo=${encodeURIComponent(`/paste-code?code=${code.trim()}&app=${app}`)}`;
    navigate(loginUrl);
  };

  return (
    <AuthLayout>
      <div className="w-full flex flex-col gap-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div
            className="flex h-14 w-14 mx-auto items-center justify-center rounded-xl"
            style={{ backgroundColor: `hsl(${branding.colors.primary} / 0.1)` }}
          >
            <KeyRound
              className="h-7 w-7"
              style={{ color: `hsl(${branding.colors.primary})` }}
            />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              Codigo de invitacion
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Ingresa el codigo que recibiste para unirte a un workspace
            </p>
          </div>
        </div>

        {/* Needs login notice */}
        {needsLogin && (
          <Alert variant="default" className="bg-blue-50 border-blue-200 text-blue-800">
            <AlertDescription>
              Necesitas iniciar sesion antes de usar tu codigo.
              <button
                type="button"
                onClick={handleGoToLogin}
                className="ml-1 font-medium underline"
              >
                Iniciar sesion
              </button>
            </AlertDescription>
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert variant="default" className="bg-green-50 border-green-200 text-green-800">
              <AlertDescription className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Te has unido exitosamente. Redirigiendo...
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-1.5">
            <Input
              id="paste-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 8).toUpperCase())}
              placeholder="AB12CD34"
              maxLength={8}
              disabled={loading || success}
              className="placeholder:text-muted-foreground/40 uppercase tracking-widest text-center text-2xl font-mono py-6"
              autoFocus
            />
          </div>

          <Button
            type="submit"
            disabled={loading || success || code.trim().length < 6}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Validando...
              </>
            ) : success ? (
              'Unido exitosamente'
            ) : (
              'Unirse con este codigo'
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground space-y-1.5">
          <div>
            <a
              href={`/join-workspace?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`}
              className="text-primary hover:underline font-medium"
            >
              Buscar workspace por nombre
            </a>
          </div>
          <div>
            <a
              href={`/login?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`}
              className="text-primary hover:underline font-medium"
            >
              Volver al inicio de sesion
            </a>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default PasteCodePage;
